<template>
  <div class="map-styler">
    <div class="p-4 bg-white rounded-lg shadow-sm">
      <h3 class="text-lg font-semibold mb-4">Map Styling</h3>
      
      <!-- Style selector -->
      <div class="mb-4">
        <label class="block text-sm font-medium text-gray-700 mb-1">Style</label>
        <div class="flex space-x-2">
          <select 
            v-model="selectedStyleId" 
            class="flex-grow p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">Default Style</option>
            <option v-for="style in styles" :key="style.id" :value="style.id">{{ style.name }}</option>
          </select>
          <button 
            @click="showStyleForm = !showStyleForm"
            class="bg-primary-500 text-white p-2 rounded-md hover:bg-primary-600 transition-colors"
          >
            <i :class="showStyleForm ? 'fas fa-times' : 'fas fa-plus'" class="mr-1"></i>
            {{ showStyleForm ? 'Cancel' : 'New' }}
          </button>
        </div>
      </div>
      
      <!-- Create/Edit Style Form -->
      <div v-if="showStyleForm" class="bg-gray-50 p-4 rounded-lg mb-4">
        <h4 class="font-medium text-gray-800 mb-2">{{ editingStyleId ? 'Edit Style' : 'Create New Style' }}</h4>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Style Name</label>
            <input 
              v-model="styleForm.name" 
              type="text" 
              class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="e.g., Paid Properties"
            >
          </div>
          
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Description</label>
            <input 
              v-model="styleForm.description" 
              type="text" 
              class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Optional description"
            >
          </div>
          
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Fill Color</label>
            <div class="flex space-x-2">
              <input 
                v-model="styleForm.fill_color" 
                type="color" 
                class="w-12 h-10 border border-gray-300 rounded-md cursor-pointer"
              >
              <input 
                v-model="styleForm.fill_color" 
                type="text" 
                class="flex-grow p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="#3388ff"
              >
            </div>
          </div>
          
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Fill Opacity</label>
            <input 
              v-model.number="styleForm.fill_opacity" 
              type="range" 
              min="0" 
              max="1" 
              step="0.1" 
              class="w-full"
            >
            <div class="text-xs text-gray-500 text-right">{{ styleForm.fill_opacity }}</div>
          </div>
          
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Stroke Color</label>
            <div class="flex space-x-2">
              <input 
                v-model="styleForm.stroke_color" 
                type="color" 
                class="w-12 h-10 border border-gray-300 rounded-md cursor-pointer"
              >
              <input 
                v-model="styleForm.stroke_color" 
                type="text" 
                class="flex-grow p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="#3388ff"
              >
            </div>
          </div>
          
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Stroke Width</label>
            <input 
              v-model.number="styleForm.stroke_width" 
              type="number" 
              min="0" 
              max="10" 
              step="0.5" 
              class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
          </div>
          
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Stroke Opacity</label>
            <input 
              v-model.number="styleForm.stroke_opacity" 
              type="range" 
              min="0" 
              max="1" 
              step="0.1" 
              class="w-full"
            >
            <div class="text-xs text-gray-500 text-right">{{ styleForm.stroke_opacity }}</div>
          </div>
          
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Point Radius</label>
            <input 
              v-model.number="styleForm.point_radius" 
              type="number" 
              min="1" 
              max="20" 
              step="1" 
              class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
          </div>
          
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Make Default</label>
            <div class="flex items-center">
              <input 
                v-model="styleForm.is_default" 
                type="checkbox" 
                class="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              >
              <span class="ml-2 text-sm text-gray-600">Set as default style</span>
            </div>
          </div>
        </div>
        
        <div class="mt-4 space-x-2 flex justify-end">
          <button 
            @click="resetForm()" 
            class="bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300 transition-colors"
          >
            Reset
          </button>
          <button 
            @click="saveStyle()" 
            class="bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 transition-colors"
          >
            {{ editingStyleId ? 'Update Style' : 'Save Style' }}
          </button>
        </div>
      </div>
      
      <!-- Style Preview -->
      <div v-if="selectedStyleId || showStyleForm" class="mb-4">
        <h4 class="text-sm font-medium text-gray-700 mb-2">Preview</h4>
        <div class="relative h-24 bg-gray-100 rounded-md overflow-hidden">
          <!-- Point -->
          <div 
            class="absolute top-1/4 left-1/4 transform -translate-x-1/2 -translate-y-1/2"
            :style="{
              width: `${previewStyle.point_radius * 2}px`,
              height: `${previewStyle.point_radius * 2}px`,
              backgroundColor: previewStyle.fill_color,
              opacity: previewStyle.fill_opacity,
              border: `${previewStyle.stroke_width}px solid ${previewStyle.stroke_color}`,
              borderRadius: '50%'
            }"
          ></div>
          
          <!-- Line -->
          <div 
            class="absolute top-1/2 left-1/4 right-3/4 transform -translate-y-1/2"
            :style="{
              height: `${previewStyle.stroke_width}px`,
              backgroundColor: previewStyle.stroke_color,
              opacity: previewStyle.stroke_opacity
            }"
          ></div>
          
          <!-- Polygon -->
          <div 
            class="absolute top-1/2 bottom-1/4 left-1/2 right-1/4 transform -translate-x-1/4"
            :style="{
              backgroundColor: previewStyle.fill_color,
              opacity: previewStyle.fill_opacity,
              border: `${previewStyle.stroke_width}px solid ${previewStyle.stroke_color}`,
              borderOpacity: previewStyle.stroke_opacity
            }"
          ></div>
        </div>
      </div>
      
      <!-- Apply Style Section -->
      <div v-if="selectedStyleId" class="pt-4 border-t border-gray-200">
        <button 
          @click="applyStyleToSelected()"
          class="w-full bg-primary-500 text-white py-2 px-4 rounded-md hover:bg-primary-600 transition-colors"
          :disabled="!hasSelectedProperties"
        >
          Apply Style to Selected Properties
        </button>
        <div v-if="!hasSelectedProperties" class="mt-2 text-sm text-gray-500 text-center">
          Select properties on the map or list to apply styling
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import axios from 'axios';
import { useAuthStore } from '../stores/auth';

const props = defineProps({
  selectedProperties: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['style-applied', 'style-created', 'style-updated']);

const authStore = useAuthStore();
const styles = ref([]);
const selectedStyleId = ref('');
const showStyleForm = ref(false);
const editingStyleId = ref(null);
const styleForm = ref({
  name: '',
  description: '',
  fill_color: '#3388ff',
  fill_opacity: 0.2,
  stroke_color: '#3388ff',
  stroke_width: 2,
  stroke_opacity: 1.0,
  point_radius: 5,
  icon_url: '',
  icon_size: 1.0,
  is_default: false
});

// Computed property to check if there are selected properties
const hasSelectedProperties = computed(() => {
  return props.selectedProperties && props.selectedProperties.length > 0;
});

// Computed property for the preview style
const previewStyle = computed(() => {
  if (selectedStyleId.value && !showStyleForm.value) {
    const style = styles.value.find(s => s.id === selectedStyleId.value);
    if (style) {
      return style;
    }
  }
  return styleForm.value;
});

// Fetch styles on component mount
onMounted(async () => {
  await fetchStyles();
});

// Watch for changes to the selected style
watch(selectedStyleId, (newStyleId) => {
  if (newStyleId && showStyleForm.value) {
    // If a style is selected and the form is open, load it for editing
    loadStyleForEditing(newStyleId);
  }
});

// Fetch styles from the API
const fetchStyles = async () => {
  try {
    const response = await axios.get('/api/data/styles', {
      headers: {
        'Authorization': `Bearer ${authStore.token}`
      }
    });
    
    if (response.data && response.data.success) {
      styles.value = response.data.styles || [];
      
      // If there's a default style, select it
      const defaultStyle = styles.value.find(s => s.is_default);
      if (defaultStyle) {
        selectedStyleId.value = defaultStyle.id;
      }
    }
  } catch (error) {
    console.error('Error fetching styles:', error);
  }
};

// Load a style for editing
const loadStyleForEditing = (styleId) => {
  const style = styles.value.find(s => s.id === styleId);
  if (style) {
    editingStyleId.value = style.id;
    styleForm.value = { ...style };
    showStyleForm.value = true;
  }
};

// Reset the style form
const resetForm = () => {
  editingStyleId.value = null;
  styleForm.value = {
    name: '',
    description: '',
    fill_color: '#3388ff',
    fill_opacity: 0.2,
    stroke_color: '#3388ff',
    stroke_width: 2,
    stroke_opacity: 1.0,
    point_radius: 5,
    icon_url: '',
    icon_size: 1.0,
    is_default: false
  };
};

// Save a style
const saveStyle = async () => {
  try {
    let response;
    
    if (editingStyleId.value) {
      // Update existing style
      response = await axios.put(`/api/data/styles/${editingStyleId.value}`, styleForm.value, {
        headers: {
          'Authorization': `Bearer ${authStore.token}`
        }
      });
      
      if (response.data && response.data.success) {
        const updatedStyle = response.data.style;
        const index = styles.value.findIndex(s => s.id === updatedStyle.id);
        if (index !== -1) {
          styles.value[index] = updatedStyle;
        }
        emit('style-updated', updatedStyle);
      }
    } else {
      // Create new style
      response = await axios.post('/api/data/styles', styleForm.value, {
        headers: {
          'Authorization': `Bearer ${authStore.token}`
        }
      });
      
      if (response.data && response.data.success) {
        const newStyle = response.data.style;
        styles.value.push(newStyle);
        selectedStyleId.value = newStyle.id;
        emit('style-created', newStyle);
      }
    }
    
    // Reset form and update UI
    resetForm();
    showStyleForm.value = false;
    
    // If this is a new default style, update other styles
    if (response.data.style.is_default) {
      styles.value.forEach(style => {
        if (style.id !== response.data.style.id) {
          style.is_default = false;
        }
      });
    }
  } catch (error) {
    console.error('Error saving style:', error);
    alert(`Failed to save style: ${error.response?.data?.message || 'Unknown error'}`);
  }
};

// Apply a style to selected properties
const applyStyleToSelected = async () => {
  if (!selectedStyleId.value || !hasSelectedProperties.value) return;
  
  try {
    const propertyIds = props.selectedProperties.map(p => p.id);
    
    const response = await axios.post(`/api/data/styles/${selectedStyleId.value}/apply`, {
      property_ids: propertyIds
    }, {
      headers: {
        'Authorization': `Bearer ${authStore.token}`
      }
    });
    
    if (response.data && response.data.success) {
      emit('style-applied', {
        styleId: selectedStyleId.value,
        propertyIds,
        count: response.data.count
      });
      
      alert(`Style applied to ${response.data.count} properties.`);
    }
  } catch (error) {
    console.error('Error applying style:', error);
    alert(`Failed to apply style: ${error.response?.data?.message || 'Unknown error'}`);
  }
};
</script>

<style scoped>
input[type="color"] {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  border: none;
  padding: 0;
}

input[type="color"]::-webkit-color-swatch-wrapper {
  padding: 0;
}

input[type="color"]::-webkit-color-swatch {
  border: none;
  border-radius: 4px;
}
</style>